/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { MenuTriggerForDirective } from '@vsolv/vectors-ui/menu';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Customer } from '@wsphere/customers/domain';
import { CustomerService } from '@wsphere/customers/web';
import { SecurityService } from '@wsphere/staff/web';
import { Claim, Warranty } from '@wsphere/warranties/domain';
import moment from 'moment';
import { BehaviorSubject, switchMap, tap } from 'rxjs';
import { FeatureFlagsService, NEW_CLAIMS_FLOW_FLAG } from '../../../policy/services/feature-flags.service';
import { WarrantyWebService } from '../../../warranty';
import { ClaimQuickAddComponent, QuickAddAttachment, QuickAddNote } from '../../components';
import { ClaimService } from '../../services';
import { isWarrantyIneligible, warrantyIneligibleMessage, warrantyIneligibleTitle } from '../../utils';

// The picked day is saved as that day in UTC, the day the server compares.
const toDate = (value: string | null | undefined) => (value ? moment.utc(value).toDate() : undefined);

const dateOrderValidator = (group: AbstractControl): ValidationErrors | null => {
  const { lossDate, dateReported } = group.value;
  return lossDate && dateReported && dateReported < lossDate ? { dateOrder: true } : null;
};

enum pages {
  WARRANTY_SELECTION = 1,
  COMPLAINT = 2,
  SUMMARY = 3,
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-open-claim-dialog',
  templateUrl: './open-claim.dialog.html',
  standalone: false,
})
export class OpenClaimDialog {
  private lastIneligibleToastAt = 0;

  /** The tenant's switch picks the new claim's flow; the previous flow asks for no intake dates. */
  private newClaimsFlow = false;

  constructor(
    private router: Router,
    private toastSvc: ToastService,
    private claimSvc: ClaimService,
    private formBuilder: FormBuilder,
    private securitySvc: SecurityService,
    private customerSvc: CustomerService,
    private warrantySvc: WarrantyWebService,
    featureFlagsSvc: FeatureFlagsService
  ) {
    featureFlagsSvc
      .isEnabledState(NEW_CLAIMS_FLOW_FLAG)
      .pipe(takeUntilDestroyed())
      .subscribe(on => (this.newClaimsFlow = on === true));
  }

  // Status label mapping for UI display
  getStatusLabel = Warranty.getStatusLabel;

  readonly isWarrantyIneligible = isWarrantyIneligible;

  @ViewChild('openClaimDialog') openClaimDialog?: DialogComponent;
  @ViewChild('quickAddComponent') quickAddComponent?: ClaimQuickAddComponent;

  @PropertyListener('claim') claim$ = new BehaviorSubject<Claim.Model | null>(null);
  @Input() claim: Claim.Model | null = null;

  @Input() set customer(customer: Customer.Model | undefined) {
    if (customer) {
      this.form.patchValue({ customer });
      if (this.form.value.warranty) this.page = pages.COMPLAINT;
    } else if (this.form.value.customer) {
      this.form.patchValue({ customer: null });
      if (!this.form.value.warranty) this.page = pages.WARRANTY_SELECTION;
    }
  }

  @Input() set warranty(warranty: Warranty.Model | null) {
    if (warranty) {
      this.setWarranty(warranty);
      if (this.form.value.customer) this.page = pages.COMPLAINT;
    } else if (this.form.value.warranty) {
      this.form.patchValue({ warranty: null });
      this.page = pages.WARRANTY_SELECTION;
    }
  }

  @Input() isCustomerPortal = false;
  @Input() pathAfterClose: string | null = null;

  @Output() closed = new EventEmitter<boolean>();
  @Output() claimId = new EventEmitter<string>();

  page = 1;
  pages = pages;

  submitting = false;

  notes: QuickAddNote[] = [];
  attachments: QuickAddAttachment[] = [];

  readonly today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  readonly maxDate = new Date();

  form = this.formBuilder.group(
    {
      customer: [null as Customer.Model | null, [Validators.required]],
      warranty: [null as Warranty.Model | null, [Validators.required]],
      complaint: ['', [Validators.required]],
      // Staff only; customers' reported date is set by the server.
      lossDate: [null as string | null],
      dateReported: [null as string | null],
      agree: [false],
    },
    { validators: dateOrderValidator }
  );

  toPickerDate(value: string | null | undefined) {
    return value ? moment(value).toDate() : null;
  }

  pickDate(control: 'lossDate' | 'dateReported', date: Date | null, trigger: MenuTriggerForDirective) {
    this.form.controls[control].setValue(date ? formatDate(date, 'yyyy-MM-dd', 'en') : null);
    trigger.close();
  }

  /** The intake dates are asked only when staff open a new claim in the new claims flow. */
  get showIntakeDates() {
    return !this.claim && !this.isCustomerPortal && this.newClaimsFlow;
  }

  claimInput$ = this.claim$.pipe(
    tap(claim => {
      if (claim) {
        this.form.patchValue({ complaint: claim.complaint });
        this.page = pages.COMPLAINT;
      } else {
        this.form.reset();
        this.page = pages.WARRANTY_SELECTION;
      }
    })
  );

  warranties: Warranty.Model[] | null = null;
  warranties$ = this.form.controls.customer.valueChanges.pipe(
    switchMap(async customer => {
      if (customer) {
        const response = await this.warrantySvc.openClaimWarrantyList({ customerId: customer.id });
        const items = response.items ?? [];
        this.warranties = this.sortWarranties(items);
      } else {
        this.warranties = null;
      }
    })
  );

  readonly isMyClaim$ = this.claim$.pipe(
    switchMap(async claim => {
      const currentUser = await this.customerSvc.retrieveSelf();
      return claim?.warranty?.customerId === currentUser?.id;
    })
  );

  canOpenClaim$ = this.isMyClaim$.pipe(
    switchMap(async isMyClaim => {
      if (isMyClaim) return true;
      else if (this.isCustomerPortal) return false;

      return await this.securitySvc.hasAccess(
        'clm_OpenClaim',
        this.securitySvc.globalDistributors$.value?.map(dist => dist.permissionKey) || null
      );
    })
  );

  openDialog(warranty?: Warranty.Model | null, customer?: Customer.Model | null) {
    if (isWarrantyIneligible(warranty ?? null)) {
      this.showWarrantyIneligibleToast(warranty ?? null);
      return;
    }

    if (customer) this.customer = customer;
    if (warranty) this.warranty = warranty;
    this.warranties = null;
    if (this.showIntakeDates && !this.form.value.dateReported) this.form.patchValue({ dateReported: this.today });
    this.openClaimDialog?.open();
  }

  closeDialog() {
    this.form.reset();
    this.closed.emit();
    this.page = 1;
    this.openClaimDialog?.close();
  }

  setWarranty(warranty: Warranty.Model) {
    this.form.patchValue({ warranty });
  }

  warrantyTheme(): ThemeColor {
    const warranty = this.form.value.warranty;
    if (!warranty) return 'default';

    return warranty.status === Warranty.Status.ACTIVATED
      ? 'success'
      : warranty.status === Warranty.Status.EXPIRED
        ? 'danger'
        : warranty.status === Warranty.Status.CANCELLED || warranty.status === Warranty.Status.VOID
          ? 'warn'
          : 'default';
  }

  private formatWarrantyStatusLabel(status: Warranty.Status): string {
    const label = this.getStatusLabel(status);
    return label ? label.charAt(0) + label.slice(1).toLowerCase() : '';
  }

  warrantyListStatusMessage(warranty: Warranty.Model): string {
    if (warranty.frozen) {
      return 'Frozen due to a payment issue. New claims are unavailable until the payment problem is resolved.';
    }

    if (warranty.status === Warranty.Status.CANCELLED) return this.formatWarrantyStatusLabel(Warranty.Status.CANCELLED);
    if (warranty.status === Warranty.Status.EXPIRED) return this.formatWarrantyStatusLabel(Warranty.Status.EXPIRED);
    if (warranty.status === Warranty.Status.VOID) return this.formatWarrantyStatusLabel(Warranty.Status.VOID);
    return this.formatWarrantyStatusLabel(Warranty.Status.DRAFT);
  }

  private showWarrantyIneligibleToast(warranty: Warranty.Model | null) {
    const now = Date.now();
    if (now - this.lastIneligibleToastAt < 1000) return;
    this.lastIneligibleToastAt = now;

    this.toastSvc.show({
      type: 'info',
      title: warrantyIneligibleTitle(warranty),
      text: warrantyIneligibleMessage(warranty),
    });
  }

  private sortWarranties(warranties: Warranty.Model[]): Warranty.Model[] {
    return [...warranties].sort((a, b) => {
      const aIneligible = isWarrantyIneligible(a);
      const bIneligible = isWarrantyIneligible(b);
      if (aIneligible === bIneligible) return 0;
      return aIneligible ? 1 : -1;
    });
  }

  addNote(note: QuickAddNote) {
    this.notes.push(note);
  }

  removeNote(note: QuickAddNote) {
    this.notes = this.notes.filter(notes => notes.title !== note.title);
  }

  addAttachment(attachment: QuickAddAttachment) {
    this.attachments.push(attachment);
  }

  removeAttachment(attachment: QuickAddAttachment) {
    this.attachments = this.attachments.filter(attachments => attachments.title !== attachment.title);
  }

  checkAgree() {
    if (this.form.value.agree) this.form.patchValue({ agree: false });
    else this.form.patchValue({ agree: true });
  }

  async submit(createItem: boolean) {
    this.submitting = true;

    const data = this.form.value;
    if (!data.warranty || !data.complaint) return;

    const claim = await this.claimSvc
      .open({
        warrantyId: data.warranty.id,
        complaint: data.complaint,
        ...(this.showIntakeDates && { lossDate: toDate(data.lossDate), dateReported: toDate(data.dateReported) }),
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.closeDialog();
      });

    if (claim) {
      await Promise.all(
        this.notes.map(async note => {
          await this.quickAddComponent?.addNote(claim.id, note);
        })
      );
      await Promise.all(
        this.attachments.map(async attachment => await this.quickAddComponent?.addAttachment(claim.id, attachment))
      );

      this.toastSvc.show({
        type: 'success',
        title: 'Claim Opened',
        text: `A new claim has been successfully created. Claim items you create will be added under this claim.`,
      });

      this.claimId.emit(claim.id);
      this.closeDialog();

      const routeToNavigate = createItem
        ? `claims/${claim.id}`
        : this.pathAfterClose
          ? this.pathAfterClose
          : `claims/${claim.id}`;

      this.router.navigate([routeToNavigate], { queryParams: { createItem: createItem } });
    }

    this.submitting = false;
  }

  async update() {
    if (!this.claim || !this.form.value.complaint) return;

    const claim = await this.claimSvc
      .update(this.claim.id, {
        complaint: this.form.value.complaint,
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.closeDialog();
      });

    if (claim) {
      this.toastSvc.show({
        type: 'success',
        title: 'Claim Updated',
        text: `<strong>Claim #${this.claim.claimNumber}</strong> has been successfully updated.`,
      });

      this.closeDialog();
    }
  }
}
